import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

type UnsubscribeState = "loading" | "success" | "already" | "error";

const UnsubscribeLead = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<UnsubscribeState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!token) {
        setState("error");
        setErrorMessage("Token manquant");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke(
          "unsubscribe-lead-email",
          {
            body: { token },
          }
        );

        if (error) {
          console.error("Unsubscribe error:", error);
          setState("error");
          setErrorMessage(error.message || "Erreur lors de la désinscription");
          return;
        }

        if (data?.success) {
          if (data.alreadyUnsubscribed) {
            setState("already");
          } else {
            setState("success");
          }
        } else {
          setState("error");
          setErrorMessage(data?.error || "Erreur inconnue");
        }
      } catch (err) {
        console.error("Unsubscribe error:", err);
        setState("error");
        setErrorMessage("Erreur de connexion");
      }
    };

    processUnsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
        {state === "loading" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-muted-foreground mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-foreground mb-2">
              Traitement en cours...
            </h1>
            <p className="text-muted-foreground">
              Veuillez patienter pendant que nous traitons votre demande.
            </p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Désinscription confirmée
            </h1>
            <p className="text-muted-foreground mb-6">
              Vous avez été retiré de notre liste de diffusion. Vous ne recevrez
              plus d'emails de notre part.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Si vous changez d'avis, vous pouvez toujours nous contacter à{" "}
              <a
                href="mailto:maxime@piktable.ch"
                className="text-primary hover:underline"
              >
                maxime@piktable.ch
              </a>
            </p>
            <Button asChild variant="outline">
              <Link to="/">Retour à l'accueil</Link>
            </Button>
          </>
        )}

        {state === "already" && (
          <>
            <Mail className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Déjà désabonné
            </h1>
            <p className="text-muted-foreground mb-6">
              Vous êtes déjà désinscrit de notre liste de diffusion. Vous ne
              recevez plus d'emails de notre part.
            </p>
            <Button asChild variant="outline">
              <Link to="/">Retour à l'accueil</Link>
            </Button>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Erreur
            </h1>
            <p className="text-muted-foreground mb-4">
              {errorMessage || "Une erreur est survenue lors de la désinscription."}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Si le problème persiste, contactez-nous à{" "}
              <a
                href="mailto:maxime@piktable.ch"
                className="text-primary hover:underline"
              >
                maxime@piktable.ch
              </a>
            </p>
            <Button asChild variant="outline">
              <Link to="/">Retour à l'accueil</Link>
            </Button>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-border">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Piktable - Réservations simplifiées
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribeLead;
